/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

export function CostCalculatorClient() {
  const [mode, setMode] = useState<"total-to-sqft" | "sqft-to-total">("total-to-sqft");
  const [totalCost, setTotalCost] = useState("");
  const [sqft, setSqft] = useState("");
  const [costPerSqft, setCostPerSqft] = useState("");

  const fmt = (n: number) =>
    n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  let result = { costPerSqft: 0, totalCost: 0, sqft: 0 };

  if (mode === "total-to-sqft") {
    const tcIn = totalCost;
    const sfIn = sqft;
    const tc = parseFloat(tcIn) || 0;
    const sf = parseFloat(sfIn) || 0;
    if (tc > 0 && sf > 0) {
      result = { costPerSqft: tc / sf, totalCost: tc, sqft: sf };
    }
  } else {
    const cpsIn = costPerSqft;
    const sfIn = sqft;
    const cps = parseFloat(cpsIn) || 0;
    const sf = parseFloat(sfIn) || 0;
    if (cps > 0 && sf > 0) {
      result = { costPerSqft: cps, totalCost: cps * sf, sqft: sf };
    }
  }

  const hasResult = result.totalCost > 0;

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      <div className="mb-6 flex gap-4">
        <button
          onClick={() => setMode("total-to-sqft")}
          className={`rounded-md px-4 py-2 text-sm font-medium ${
            mode === "total-to-sqft" ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
        >
          Total Cost → Per Sq Ft
        </button>
        <button
          onClick={() => setMode("sqft-to-total")}
          className={`rounded-md px-4 py-2 text-sm font-medium ${
            mode === "sqft-to-total" ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
        >
          Per Sq Ft → Total Cost
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {mode === "total-to-sqft" ? (
          <div>
            <label className="block text-sm font-medium text-foreground">Total Project Cost ($)</label>
            <input
              type="number"
              value={totalCost}
              onChange={(e) => setTotalCost(e.target.value)}
              placeholder="300000"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            />
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-foreground">Cost Per Sq Ft ($)</label>
            <input
              type="number"
              value={costPerSqft}
              onChange={(e) => setCostPerSqft(e.target.value)}
              placeholder="175"
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-foreground">Square Footage</label>
          <input
            type="number"
            value={sqft}
            onChange={(e) => setSqft(e.target.value)}
            placeholder="2000"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          />
        </div>
      </div>

      {hasResult && (
        <>
          <div className="mt-6 grid gap-4 rounded-lg bg-muted/50 p-4 sm:grid-cols-3">
            <div>
              <div className="text-sm text-muted-foreground">Total Cost</div>
              <div className="text-2xl font-bold text-foreground">${fmt(result.totalCost)}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Cost Per Sq Ft</div>
              <div className="text-2xl font-bold text-secondary">${fmt(result.costPerSqft)}</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Square Footage</div>
              <div className="text-2xl font-bold text-foreground">{result.sqft.toLocaleString()}</div>
            </div>
          </div>
          <ResultActions
            toolSlug="cost-calculator"
            inputs={{ mode, totalCost, sqft, costPerSqft }}
            outputs={{ costPerSqFt: result.costPerSqft, totalCost: result.totalCost, squareFootage: result.sqft }}
          />
        </>
      )}
    </div>
  );
}
